import React, { useState, useEffect } from 'react';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import { API_CONFIG } from '../config';
import { updateUserPreferences, getUserPreferences } from '../utils/userPreferences';
import Button from './ui/Button';
import ThemeEditor from './ThemeEditor';
import UserManagementTab from './UserManagementTab';
import ActivityLogsTab from './ActivityLogsTab';
import MediaTrashBinPanel from './MediaTrashBinPanel';
import BiddingTrashBinPanel from './BiddingTrashBinPanel';

interface SystemMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onDataRefresh?: () => void;
  currentRole?: string;
}

type TabType = 'ui' | 'users' | 'backup' | 'activity-logs' | 'vfx' | 'themes' | 'changelog' | 'about';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ComponentType<any>;
  description: string;
  requiresSuperAdmin?: boolean;
  requiresAdminOrHigher?: boolean;
}

const SystemMenu: React.FC<SystemMenuProps> = ({
  isOpen,
  onClose,
  onDataRefresh,
  currentRole
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('ui');
  const [uiScale, setUiScale] = useState(1);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [databaseStats, setDatabaseStats] = useState<any>(null);
  const [studioName, setStudioName] = useState('');
  const [studioNameSaved, setStudioNameSaved] = useState(false);
  const [adminSeeAllProjects, setAdminSeeAllProjects] = useState(false);
  const [backupZipLoading, setBackupZipLoading] = useState(false);

  // Initialize theme and scale from localStorage
  useEffect(() => {
    if (isOpen) {
      const savedScale = localStorage.getItem('spellbook_ui_scale');
      const savedTheme = localStorage.getItem('spellbook_theme');
      
      if (savedScale) {
        const scale = parseFloat(savedScale);
        setUiScale(scale);
        document.documentElement.style.setProperty('--ui-scale', scale.toString());
      }
      
      if (savedTheme) {
        const isDark = savedTheme === 'dark';
        setIsDarkTheme(isDark);
        document.documentElement.className = isDark ? 'dark' : 'light';
      } else {
        // Default to dark theme
        setIsDarkTheme(true);
        document.documentElement.className = 'dark';
      }

      // Load database statistics
      loadDatabaseStats();
      
      // Load studio name for MagicBox integration
      loadStudioName();

      if (currentRole === 'Admin') {
        setAdminSeeAllProjects(getUserPreferences().adminSeeAllProjects === true);
      }
    }
  }, [isOpen, currentRole]);

  const handleThemeToggle = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    document.documentElement.className = newTheme ? 'dark' : 'light';
    updateUserPreferences({ theme: newTheme ? 'dark' : 'light' });
  };

  const handleScaleChange = (scale: number) => {
    setUiScale(scale);
    document.documentElement.style.setProperty('--ui-scale', scale.toString());
    updateUserPreferences({ uiScale: scale });
  };

  // Load studio name for MagicBox integration
  const loadStudioName = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/settings/studio-name`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setStudioName(data.studioName || '');
      }
    } catch (error) {
      console.error('Failed to load studio name:', error);
      // Fallback to localStorage
      const saved = localStorage.getItem('spellbook_studio_name');
      if (saved) {
        setStudioName(saved);
      }
    }
  };

  // Save studio name for MagicBox integration
  const saveStudioName = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/settings/studio-name`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ studioName }),
      });
      
      if (response.ok) {
        // Also save to localStorage as backup
        localStorage.setItem('spellbook_studio_name', studioName);
        setStudioNameSaved(true);
        setTimeout(() => setStudioNameSaved(false), 3000);
      } else {
        alert('Failed to save studio name');
      }
    } catch (error) {
      console.error('Failed to save studio name:', error);
      // Fallback to localStorage only
      localStorage.setItem('spellbook_studio_name', studioName);
      setStudioNameSaved(true);
      setTimeout(() => setStudioNameSaved(false), 3000);
    }
  };

  // Load database statistics
  const loadDatabaseStats = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/backup/info`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDatabaseStats(data.databaseStats);
      }
    } catch (error) {
      console.error('Failed to load database stats:', error);
    }
  };

  const tabs: TabConfig[] = [
    {
      id: 'ui',
      label: t('system.ui.title'),
      icon: isDarkTheme ? ICONS.Moon : ICONS.Sun,
      description: t('system.ui.description')
    },
    {
      id: 'users',
      label: t('system.users.title'),
      icon: ICONS.Users,
      description: t('system.users.description'),
      requiresAdminOrHigher: true  // Admin + Super Admin
    },
    {
      id: 'themes',
      label: t('system.themes.title'),
      icon: ICONS.Palette,
      description: t('system.themes.description'),
      requiresAdminOrHigher: true  // Admin + Super Admin
    },
   {
     id: 'backup',
     label: t('system.backup.title'),
     icon: ICONS.Database,
     description: t('system.backup.description'),
     requiresSuperAdmin: true  // Only Super Admin
   },
   {
     id: 'activity-logs',
     label: t('system.activityLogs.title'),
     icon: ICONS.BookOpen,
     description: t('system.activityLogs.description'),
     requiresAdminOrHigher: true  // Admin + Super Admin
   },
   {
     id: 'vfx',
     label: t('system.vfx.title'),
     icon: ICONS.Monitor,
     description: t('system.vfx.description'),
     requiresSuperAdmin: true  // Only Super Admin
   },
   {
     id: 'changelog',
     label: t('system.changelog.title'),
     icon: ICONS.BookOpen,
     description: t('system.changelog.description')
   },
    {
      id: 'about',
      label: t('system.about.title'),
      icon: ICONS.Info,
      description: t('system.about.description')
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'ui':
        return (
          <div className="space-y-6">
            {/* Theme Settings */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <ICONS.Settings className="w-5 h-5 mr-2" />
                {t('system.ui.theme.title')}
              </h3>
              
              <div className="space-y-4">
                 <div className="flex items-center justify-between">
                   <div>
                     <label className="text-sm font-medium text-gray-300">
                       {t('system.ui.theme.current')}
                     </label>
                     <p className="text-xs text-gray-400">
                       {isDarkTheme ? t('system.ui.theme.dark') : t('system.ui.theme.light')}
                     </p>
                   </div>
                  <button
                    onClick={handleThemeToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      isDarkTheme ? 'bg-indigo-600' : 'bg-gray-400'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                        isDarkTheme ? 'translate-x-6 bg-white' : 'translate-x-1 bg-gray-200'
                      }`}
                    />
                  </button>
                </div>
              </div>

            </div>

            {currentRole === 'Admin' && (
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {t('system.admin.projectAccess.title')}
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">
                      {t('system.admin.projectAccess.seeAllProjects')}
                    </label>
                    <p className="text-xs text-gray-400">
                      {t('system.admin.projectAccess.seeAllProjectsHint')}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const next = !adminSeeAllProjects;
                      setAdminSeeAllProjects(next);
                      updateUserPreferences({ adminSeeAllProjects: next });
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      adminSeeAllProjects ? 'bg-indigo-600' : 'bg-gray-400'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                        adminSeeAllProjects ? 'translate-x-6 bg-white' : 'translate-x-1 bg-gray-200'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}

            {/* UI Scale Settings */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <ICONS.Monitor className="w-5 h-5 mr-2" />
                {t('system.ui.scaling.title')}
              </h3>
              
              <div className="space-y-4">
                 <div>
                   <label className="text-sm font-medium text-gray-300">
                     {Math.round(uiScale * 100)}%
                   </label>
                  <p className="text-xs text-gray-400 mb-3">
                    {t('system.ui.scaling.description')}
                  </p>
                  
                  <div className="space-y-3">
                    <input
                      type="range"
                      min="0.5"
                      max="1.5"
                      step="0.1"
                      value={uiScale}
                      onChange={(e) => handleScaleChange(parseFloat(e.target.value))}
                      className="slider w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                    />
                    
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>50%</span>
                      <span>100%</span>
                      <span>150%</span>
                    </div>
                    
                    <div className="flex gap-2">
                     <Button
                       onClick={() => handleScaleChange(0.8)}
                       variant="secondary"
                       size="sm"
                       className="flex-1 bg-gray-600 hover:bg-gray-500 border border-gray-500"
                     >
                       80%
                     </Button>
                     <Button
                       onClick={() => handleScaleChange(1.0)}
                       variant="secondary"
                       size="sm"
                       className="flex-1 bg-gray-600 hover:bg-gray-500 border border-gray-500"
                     >
                       100%
                     </Button>
                     <Button
                       onClick={() => handleScaleChange(1.2)}
                       variant="secondary"
                       size="sm"
                       className="flex-1 bg-gray-600 hover:bg-gray-500 border border-gray-500"
                     >
                       120%
                     </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            {/* Build Information */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <ICONS.Info className="w-5 h-5 mr-2" />
                {t('system.about.buildInfo.title')}
              </h3>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">{t('system.about.buildInfo.version')}:</span>
                    <span className="text-white ml-2">2.0.0</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('system.about.buildInfo.buildDate')}:</span>
                  <span className="text-white ml-2">{new Date().toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('system.about.buildInfo.buildTime')}:</span>
                  <span className="text-white ml-2">{new Date().toLocaleTimeString()}</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('system.about.buildInfo.environment')}:</span>
                  <span className="text-white ml-2">Production</span>
                </div>
              </div>
            </div>
          </div>
         );

      case 'users':
        if (currentRole !== 'Super Admin' && currentRole !== 'Admin' && currentRole !== 'Producer') {
          return (
            <div className="text-center py-8">
              <ICONS.Lock className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('system.vfx.accessDenied')}
              </h3>
              <p className="text-gray-300">
                {t('system.vfx.accessDeniedDescription')}
              </p>
            </div>
          );
        }
        return <UserManagementTab onDataRefresh={onDataRefresh} currentRole={currentRole} />;
       
       case 'themes':
         if (currentRole !== 'Super Admin' && currentRole !== 'Admin') {
           return (
             <div className="text-center py-8">
               <ICONS.Lock className="w-12 h-12 text-gray-500 mx-auto mb-4" />
               <h3 className="text-lg font-semibold text-white mb-2">
                 {t('system.vfx.accessDenied')}
               </h3>
               <p className="text-gray-300">
                 {t('system.vfx.accessDeniedDescription')}
               </p>
             </div>
           );
         }
         
         return (
           <ThemeEditor
             isOpen={true}
             onClose={() => {}}
             onThemeChange={() => {}}
           />
         );
       
      case 'backup':
       if (currentRole !== 'Super Admin') {
         return (
            <div className="text-center py-8">
              <ICONS.Lock className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('system.vfx.accessDenied')}
              </h3>
              <p className="text-gray-300">
                {t('system.backup.accessDeniedDescription')}
              </p>
            </div>
         );
       }
        
        return (
          <div className="space-y-6">
            {/* Database Statistics */}
             <div className="bg-gray-700 rounded-lg p-6">
               <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                 <ICONS.Database className="w-5 h-5 mr-2" />
                 {t('system.backup.stats')}
               </h3>
               <div className="grid grid-cols-2 gap-4 text-sm">
                 <div className="flex justify-between">
                   <span className="text-gray-300">{t('system.backup.users')}:</span>
                   <span className="text-white font-mono">{databaseStats?.users || 0}</span>
                 </div>
                 <div className="flex justify-between">
                   <span className="text-gray-300">{t('system.backup.artists')}:</span>
                   <span className="text-white font-mono">{databaseStats?.artists || 0}</span>
                 </div>
                 <div className="flex justify-between">
                   <span className="text-gray-300">{t('system.backup.projects')}:</span>
                   <span className="text-white font-mono">{databaseStats?.projects || 0}</span>
                 </div>
                 <div className="flex justify-between">
                   <span className="text-gray-300">{t('system.backup.assignments')}:</span>
                   <span className="text-white font-mono">{databaseStats?.artist_projects || 0}</span>
                 </div>
               </div>
             </div>

            {/* Backup Actions */}
             <div className="bg-gray-700 rounded-lg p-6">
               <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                 <ICONS.Download className="w-5 h-5 mr-2" />
                 {t('system.backup.management')}
               </h3>
               
               <div className="space-y-4">
                 <p className="text-sm text-gray-300">
                   {t('system.backup.instructions')}
                 </p>
                 <p className="text-xs text-amber-300">
                   {t('system.backup.zipHint')}
                 </p>
                
                <div className="flex flex-wrap gap-3">
                   <Button
                     onClick={async () => {
                       setBackupZipLoading(true);
                       try {
                         const token = localStorage.getItem('spellbook_token');
                         const response = await fetch(`${API_CONFIG.API_URL}/backup/export-full-zip`, {
                           method: 'GET',
                           headers: {
                             'Authorization': `Bearer ${token}`,
                           }
                         });

                         if (response.ok) {
                           const contentDisposition = response.headers.get('Content-Disposition');
                           const savedPath = response.headers.get('X-Backup-Path');
                           const filename = contentDisposition
                             ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
                             : `spellbook-full-backup-${new Date().toISOString().split('T')[0]}.zip`;

                           const blob = await response.blob();
                           const url = window.URL.createObjectURL(blob);
                           const a = document.createElement('a');
                           a.href = url;
                           a.download = filename;
                           document.body.appendChild(a);
                           a.click();
                           window.URL.revokeObjectURL(url);
                           document.body.removeChild(a);

                           alert(
                             savedPath
                               ? `${t('system.backup.zipSuccess')}: ${filename}\n${t('system.backup.savedOnServer')}: ${savedPath}`
                               : `${t('system.backup.zipSuccess')}: ${filename}`
                           );
                         } else {
                           const errorData = await response.json().catch(() => ({}));
                           alert(`${t('system.backup.zipFailed')}: ${errorData.error || response.status}`);
                         }
                       } catch (err) {
                         alert(t('system.backup.connectionError'));
                       } finally {
                         setBackupZipLoading(false);
                       }
                     }}
                     variant="primary"
                     disabled={backupZipLoading}
                     className="flex items-center bg-indigo-600 hover:bg-indigo-700 border border-indigo-500"
                   >
                     <ICONS.Download className="w-4 h-4 mr-2" />
                     {backupZipLoading ? t('system.backup.zipLoading') : t('system.backup.downloadZip')}
                   </Button>

                   <Button
                     onClick={async () => {
                       try {
                         const token = localStorage.getItem('spellbook_token');
                         const response = await fetch(`${API_CONFIG.API_URL}/backup/export`, {
                           method: 'GET',
                           headers: {
                             'Authorization': `Bearer ${token}`,
                           }
                         });

                         if (response.ok) {
                           const contentDisposition = response.headers.get('Content-Disposition');
                           const filename = contentDisposition
                             ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
                             : `spellbook-backup-${new Date().toISOString().split('T')[0]}.json`;

                           const blob = await response.blob();
                           const url = window.URL.createObjectURL(blob);
                           const a = document.createElement('a');
                           a.href = url;
                           a.download = filename;
                           document.body.appendChild(a);
                           a.click();
                           window.URL.revokeObjectURL(url);
                           document.body.removeChild(a);

                           alert(`Backup exported successfully as ${filename}`);
                         } else {
                           const errorData = await response.json();
                           alert(`Failed to export backup: ${errorData.error}`);
                         }
                       } catch (err) {
                         alert(t('system.backup.connectionError'));
                       }
                     }}
                     variant="secondary"
                     className="flex items-center bg-gray-600 hover:bg-gray-500 border border-gray-500"
                   >
                     <ICONS.Download className="w-4 h-4 mr-2" />
                     {t('system.backup.exportJson')}
                   </Button>
                   
                   <Button
                     onClick={() => {
                       const input = document.createElement('input');
                       input.type = 'file';
                       input.accept = '.json';
                       input.onchange = async (e) => {
                         const file = (e.target as HTMLInputElement).files?.[0];
                         if (file) {
                           try {
                             const token = localStorage.getItem('spellbook_token');
                             const formData = new FormData();
                             formData.append('backupFile', file);

                             const response = await fetch(`${API_CONFIG.API_URL}/backup/import`, {
                               method: 'POST',
                               headers: {
                                 'Authorization': `Bearer ${token}`,
                               },
                               body: formData
                             });

                             if (response.ok) {
                               const result = await response.json();
                               alert(`Backup imported successfully! Records: ${result.recordsImported}`);
                               window.location.reload();
                             } else {
                               const errorData = await response.json();
                               alert(`Failed to import backup: ${errorData.error}`);
                             }
                           } catch (error) {
                             alert(t('system.backup.connectionError'));
                           }
                         }
                       };
                       input.click();
                     }}
                     variant="secondary"
                     className="flex items-center bg-gray-600 hover:bg-gray-500 border border-gray-500"
                   >
                     <ICONS.Upload className="w-4 h-4 mr-2" />
                     {t('system.backup.importJson')}
                   </Button>
                </div>
                
                 <p className="text-xs text-gray-400">
                   {t('system.backup.formats')}
                 </p>
              </div>
            </div>

            <MediaTrashBinPanel />
            <BiddingTrashBinPanel />
          </div>
        );
      
     case 'activity-logs':
       if (currentRole !== 'Super Admin' && currentRole !== 'Admin') {
         return (
           <div className="text-center py-8">
             <ICONS.Lock className="w-12 h-12 text-gray-500 mx-auto mb-4" />
             <h3 className="text-lg font-semibold text-white mb-2">
               {t('system.vfx.accessDenied')}
             </h3>
             <p className="text-gray-300">
               {t('system.vfx.accessDeniedDescription')}
             </p>
           </div>
         );
       }
       
       return <ActivityLogsTab />;
      
      case 'vfx':
        if (currentRole !== 'Super Admin') {
          return (
             <div className="text-center py-8">
               <ICONS.Lock className="w-12 h-12 text-gray-500 mx-auto mb-4" />
               <h3 className="text-lg font-semibold text-white mb-2">
                 {t('system.vfx.accessDenied')}
               </h3>
               <p className="text-gray-300">
                 {t('system.vfx.accessDeniedDescription')}
               </p>
             </div>
          );
        }
        
         return (
           <div className="space-y-6">
             {/* Studio Configuration */}
             <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg p-6 border border-blue-500/30">
               <h3 className="text-lg font-semibold text-white flex items-center mb-4">
                 <ICONS.Settings className="w-5 h-5 text-blue-400 mr-2" />
                 🏢 {t('system.studio.title')}
               </h3>
               
               <div className="space-y-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-300 mb-2">
                     {t('system.studio.studioName')}
                   </label>
                   <p className="text-xs text-gray-400 mb-3">
                     {t('system.studio.studioNameDescription')}
                   </p>
                   <div className="flex gap-2">
                     <input
                       type="text"
                       value={studioName}
                       onChange={(e) => setStudioName(e.target.value)}
                       placeholder={t('system.studio.studioNamePlaceholder')}
                       className="flex-1 px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                     />
                     <button
                       onClick={saveStudioName}
                       className={`px-6 py-2 rounded-lg font-medium transition-all ${
                         studioNameSaved
                           ? 'bg-green-600 text-white'
                           : 'bg-blue-600 hover:bg-blue-700 text-white'
                       }`}
                     >
                       {studioNameSaved ? `✓ ${t('system.studio.saved')}` : t('system.studio.save')}
                     </button>
                   </div>
                   {studioName && (
                     <p className="mt-2 text-xs text-green-400">
                       ✓ Current studio name: <strong>{studioName}</strong>
                     </p>
                   )}
                 </div>
                 
                 <div className="p-3 bg-blue-900/20 rounded border border-blue-500/30">
                   <p className="text-xs text-blue-300">
                     <strong>📌 {t('system.studio.note')}:</strong> {t('system.studio.noteText')} <code className="text-green-400">GET /api/v1/settings/studio-name</code> {t('system.studio.toIdentify')}
                   </p>
                 </div>
               </div>
             </div>

             {/* Authentication & System */}
             <div className="space-y-4">
               <h3 className="text-lg font-semibold text-white flex items-center">
                 <ICONS.Monitor className="w-5 h-5 text-green-500 mr-2" />
                 Аутентификация и система
               </h3>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="p-4 bg-gray-700 rounded-lg border border-blue-600">
                   <h4 className="font-medium text-blue-200 mb-2">GET /api/v1/settings/studio-name</h4>
                   <p className="text-sm text-gray-300 mb-2">Получить имя студии</p>
                   <p className="text-xs text-gray-400">Для handshake с LightBox</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-blue-600">
                   <h4 className="font-medium text-blue-200 mb-2">POST /api/v1/settings/studio-name</h4>
                   <p className="text-sm text-gray-300 mb-2">Установить имя студии</p>
                   <p className="text-xs text-gray-400">Body: {`{studioName: string}`}</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-green-200 mb-2">POST /api/auth/login</h4>
                   <p className="text-sm text-gray-300 mb-2">Вход в систему</p>
                   <p className="text-xs text-gray-400">Body: {`{email, password}`}</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-green-200 mb-2">POST /api/auth/forgot-password</h4>
                   <p className="text-sm text-gray-300 mb-2">Восстановление пароля</p>
                   <p className="text-xs text-gray-400">Body: {`{email}`}</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-green-200 mb-2">GET /api/</h4>
                   <p className="text-sm text-gray-300 mb-2">Проверка состояния API</p>
                   <p className="text-xs text-gray-400">Возвращает статус сервера</p>
                 </div>
               </div>
             </div>

             {/* Artists Management */}
             <div className="space-y-4">
               <h3 className="text-lg font-semibold text-white flex items-center">
                 <ICONS.Users className="w-5 h-5 text-blue-500 mr-2" />
                 Управление художниками
               </h3>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-blue-200 mb-2">GET /api/artists</h4>
                   <p className="text-sm text-gray-300 mb-2">Получить список художников</p>
                   <p className="text-xs text-gray-400">Query: ?specialization=3D&software=Maya</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-blue-200 mb-2">GET /api/artists/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Получить художника по ID</p>
                   <p className="text-xs text-gray-400">Path: /api/artists/123</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-blue-200 mb-2">POST /api/artists</h4>
                   <p className="text-sm text-gray-300 mb-2">Создать нового художника</p>
                   <p className="text-xs text-gray-400">FormData с аватаром</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-blue-200 mb-2">PUT /api/artists/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Обновить художника</p>
                   <p className="text-xs text-gray-400">FormData с аватаром</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-blue-200 mb-2">DELETE /api/artists/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Удалить художника</p>
                   <p className="text-xs text-gray-400">Требует права на удаление</p>
                 </div>
               </div>
             </div>

             {/* Projects Management */}
             <div className="space-y-4">
               <h3 className="text-lg font-semibold text-white flex items-center">
                 <ICONS.Briefcase className="w-5 h-5 text-purple-500 mr-2" />
                 Управление проектами
               </h3>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-purple-200 mb-2">GET /api/projects</h4>
                   <p className="text-sm text-gray-300 mb-2">Получить список проектов</p>
                   <p className="text-xs text-gray-400">Доступно: Super Admin, Producer</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-purple-200 mb-2">GET /api/projects/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Получить проект по ID</p>
                   <p className="text-xs text-gray-400">Доступно: Super Admin, Producer</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-purple-200 mb-2">GET /api/projects/:id/artists</h4>
                   <p className="text-sm text-gray-300 mb-2">Получить художников проекта</p>
                   <p className="text-xs text-gray-400">Доступно: Super Admin, Producer</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-purple-200 mb-2">POST /api/projects</h4>
                   <p className="text-sm text-gray-300 mb-2">Создать проект</p>
                   <p className="text-xs text-gray-400">Только Super Admin</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-purple-200 mb-2">PUT /api/projects/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Обновить проект</p>
                   <p className="text-xs text-gray-400">Только Super Admin</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-purple-200 mb-2">DELETE /api/projects/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Удалить проект</p>
                   <p className="text-xs text-gray-400">Только Super Admin</p>
                 </div>
               </div>
             </div>

             {/* VFX Planner Integration */}
             <div className="space-y-4">
               <h3 className="text-lg font-semibold text-white flex items-center">
                 <ICONS.Monitor className="w-5 h-5 text-indigo-500 mr-2" />
                 LightBox API (VFX Planner)
               </h3>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 {/* Dashboard */}
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">GET /api/v1/vfx/dashboard</h4>
                   <p className="text-sm text-gray-300 mb-2">Дашборд VFX планировщика</p>
                   <p className="text-xs text-gray-400">Общая статистика, пользователи, проекты, художники</p>
                 </div>
                 
                 {/* Users */}
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">GET /api/v1/vfx/users</h4>
                   <p className="text-sm text-gray-300 mb-2">Список пользователей VFX</p>
                   <p className="text-xs text-gray-400">Для интеграции с LightBox</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">GET /api/v1/vfx/users/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Получить пользователя по ID</p>
                   <p className="text-xs text-gray-400">Детальная информация о пользователе</p>
                 </div>
                 
                {/* Projects */}
                <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                  <h4 className="font-medium text-indigo-200 mb-2">GET /api/v1/vfx/projects</h4>
                  <p className="text-sm text-gray-300 mb-2">Список проектов VFX</p>
                  <p className="text-xs text-gray-400">Для синхронизации с LightBox + новые поля</p>
                  <div className="mt-2 space-y-1 text-xs text-green-400">
                    <div>✨ image_url - изображение проекта</div>
                    <div>✨ producer_id - назначенный Producer</div>
                    <div>✨ manager_id - назначенный PM/Supervisor</div>
                    <div>✨ createdByUsername - создатель проекта</div>
                  </div>
                </div>
                
                <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                  <h4 className="font-medium text-indigo-200 mb-2">GET /api/v1/vfx/projects/:id</h4>
                  <p className="text-sm text-gray-300 mb-2">Получить проект по ID</p>
                  <p className="text-xs text-gray-400">Детальная информация + управление</p>
                </div>
                 
                 {/* Assignments */}
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">GET /api/v1/vfx/projects/:id/assignments</h4>
                   <p className="text-sm text-gray-300 mb-2">Назначения по проекту</p>
                   <p className="text-xs text-gray-400">Управление ресурсами проекта</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">GET /api/v1/vfx/artists/:id/assignments</h4>
                   <p className="text-sm text-gray-300 mb-2">Назначения художника</p>
                   <p className="text-xs text-gray-400">Проекты конкретного художника</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">POST /api/v1/vfx/assignments</h4>
                   <p className="text-sm text-gray-300 mb-2">Создать назначение</p>
                   <p className="text-xs text-gray-400">Назначить художника на проект</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">PUT /api/v1/vfx/assignments/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Обновить назначение</p>
                   <p className="text-xs text-gray-400">Изменить параметры назначения</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">DELETE /api/v1/vfx/assignments/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Удалить назначение</p>
                   <p className="text-xs text-gray-400">Отменить назначение художника</p>
                 </div>
                 
                 {/* Availability */}
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">GET /api/v1/vfx/artists/:id/availability</h4>
                   <p className="text-sm text-gray-300 mb-2">Доступность художника</p>
                   <p className="text-xs text-gray-400">Календарь доступности</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-indigo-200 mb-2">PUT /api/v1/vfx/artists/:id/availability</h4>
                   <p className="text-sm text-gray-300 mb-2">Обновить доступность</p>
                   <p className="text-xs text-gray-400">Изменить календарь доступности</p>
                 </div>
               </div>
               
               <div className="mt-6 p-4 bg-indigo-900/20 rounded-lg border border-indigo-500/30">
                 <h4 className="font-medium text-indigo-200 mb-2">🔐 Аутентификация</h4>
                 <p className="text-sm text-gray-300 mb-2">Все endpoints требуют Bearer token в заголовке Authorization</p>
                 <p className="text-xs text-gray-400">Получить token: POST /api/v1/auth/login</p>
               </div>
             </div>

             {/* Backup & Export */}
             <div className="space-y-4">
               <h3 className="text-lg font-semibold text-white flex items-center">
                 <ICONS.Database className="w-5 h-5 text-orange-500 mr-2" />
                 Резервное копирование и экспорт
               </h3>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-orange-200 mb-2">GET /api/backup/info</h4>
                   <p className="text-sm text-gray-300 mb-2">Информация о бэкапе</p>
                   <p className="text-xs text-gray-400">Только Super Admin</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-orange-200 mb-2">GET /api/backup/export-full-zip</h4>
                   <p className="text-xs text-gray-400 mb-3">Full snapshot ZIP (DB + referenced media). Super Admin only.</p>
                   <h4 className="font-medium text-orange-200 mb-2">GET /api/backup/export</h4>
                   <p className="text-sm text-gray-300 mb-2">Экспорт базы данных</p>
                   <p className="text-xs text-gray-400">JSON файл с данными</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-orange-200 mb-2">POST /api/backup/import</h4>
                   <p className="text-sm text-gray-300 mb-2">Импорт базы данных</p>
                   <p className="text-xs text-gray-400">FormData с JSON файлом</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-orange-200 mb-2">GET /api/export/artists/csv</h4>
                   <p className="text-sm text-gray-300 mb-2">Экспорт художников в CSV</p>
                   <p className="text-xs text-gray-400">Фильтрация по параметрам</p>
                 </div>
               </div>
             </div>

             {/* User Management */}
             <div className="space-y-4">
               <h3 className="text-lg font-semibold text-white flex items-center">
                 <ICONS.Users className="w-5 h-5 text-red-500 mr-2" />
                 Управление пользователями
               </h3>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-red-200 mb-2">GET /api/auth/users</h4>
                   <p className="text-sm text-gray-300 mb-2">Список пользователей</p>
                   <p className="text-xs text-gray-400">Только Super Admin</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-red-200 mb-2">POST /api/auth/users</h4>
                   <p className="text-sm text-gray-300 mb-2">Создать пользователя</p>
                   <p className="text-xs text-gray-400">Только Super Admin</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-red-200 mb-2">DELETE /api/auth/users/:id</h4>
                   <p className="text-sm text-gray-300 mb-2">Удалить пользователя</p>
                   <p className="text-xs text-gray-400">Только Super Admin</p>
                 </div>
                 
                 <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                   <h4 className="font-medium text-red-200 mb-2">GET /api/auth/generate-password</h4>
                   <p className="text-sm text-gray-300 mb-2">Сгенерировать пароль</p>
                   <p className="text-xs text-gray-400">Только Super Admin</p>
                 </div>
               </div>
             </div>

            <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <div className="flex items-start space-x-3">
                <ICONS.Info className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-200 mb-1">Интеграция с LightBox</h4>
                  <p className="text-sm text-blue-300 mb-2">
                    Все API endpoints готовы для интеграции с LightBox. Используйте VFX Planner API для синхронизации проектов, управления назначениями и отслеживания доступности художников.
                  </p>
                  <p className="text-xs text-blue-400">
                    📄 Полная документация API: <code className="text-green-300">LIGHTBOX_API_INTEGRATION.md</code>
                  </p>
                  <p className="text-xs text-blue-400 mt-1">
                    🔄 API Version: <strong>2.0.0</strong> (December 2025)
                  </p>
                </div>
              </div>
            </div>
           </div>
         );
      
      case 'about':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <ICONS.Info className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                VFX Spellbook
              </h3>
              <p className="text-gray-400 mb-4">
                {t('system.about.version')} 2.0.0
              </p>
              
              {/* Version and Build Info */}
              <div className="p-4 bg-gray-700 rounded-lg border border-gray-600 text-left">
                <h4 className="font-semibold text-white mb-3">
                  {t('system.about.buildInfo.title')}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('system.about.buildInfo.version')}:</span>
                    <span className="text-white font-mono">2.0.0</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('system.about.buildInfo.buildDate')}:</span>
                    <span className="text-white font-mono">{new Date().toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('system.about.buildInfo.buildTime')}:</span>
                    <span className="text-white font-mono">{new Date().toLocaleTimeString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">{t('system.about.buildInfo.environment')}:</span>
                    <span className="text-white font-mono">Production</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        );

      case 'changelog':
        return (
          <div className="space-y-6">
            {/* Version 2.0 */}
            <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-lg border border-purple-500/30 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-purple-200 flex items-center">
                  <ICONS.Info className="w-6 h-6 mr-2" />
                  {t('system.changelog.version')} 2.0.0
                </h3>
                <span className="text-sm text-purple-400 bg-purple-500/20 px-3 py-1 rounded-full">
                  {t('system.changelog.current')}
                </span>
              </div>
              <p className="text-gray-400 text-sm mb-4">December 2025</p>

              <div className="space-y-4">
                {/* MagicBox Integration */}
                <div className="bg-gray-800/50 p-4 rounded border border-purple-500/20">
                  <h4 className="font-medium text-purple-300 mb-3 flex items-center">
                    <ICONS.Link className="w-5 h-5 mr-2" />
                    {t('system.changelog.magicbox.title')}
                  </h4>
                  <ul className="text-gray-300 space-y-2 text-sm ml-7 list-disc">
                    <li>{t('system.changelog.magicbox.syncComments')}</li>
                    <li>{t('system.changelog.magicbox.pullStatuses')}</li>
                    <li>{t('system.changelog.magicbox.shotMapping')}</li>
                    <li>{t('system.changelog.magicbox.syncHistory')}</li>
                  </ul>
                </div>

                {/* Financial System */}
                <div className="bg-gray-800/50 p-4 rounded border border-green-500/20">
                  <h4 className="font-medium text-green-300 mb-3 flex items-center">
                    <ICONS.Calculator className="w-5 h-5 mr-2" />
                    {t('system.changelog.finance.title')}
                  </h4>
                  <ul className="text-gray-300 space-y-2 text-sm ml-7 list-disc">
                    <li>{t('system.changelog.finance.transactions')}</li>
                    <li>{t('system.changelog.finance.progressBars')}</li>
                    <li>{t('system.changelog.finance.liabilities')}</li>
                    <li>{t('system.changelog.finance.grouping')}</li>
                    <li>{t('system.changelog.finance.artistNames')}</li>
                  </ul>
                </div>

                {/* Bidding Improvements */}
                <div className="bg-gray-800/50 p-4 rounded border border-blue-500/20">
                  <h4 className="font-medium text-blue-300 mb-3 flex items-center">
                    <ICONS.BookOpen className="w-5 h-5 mr-2" />
                    {t('system.changelog.bidding.title')}
                  </h4>
                  <ul className="text-gray-300 space-y-2 text-sm ml-7 list-disc">
                    <li>{t('system.changelog.bidding.conflicts')}</li>
                    <li>{t('system.changelog.bidding.paidIndicators')}</li>
                    <li>{t('system.changelog.bidding.duplication')}</li>
                    <li>{t('system.changelog.bidding.contextMenu')}</li>
                    <li>{t('system.changelog.bidding.autoFees')}</li>
                  </ul>
                </div>

                {/* Scene Detection */}
                <div className="bg-gray-800/50 p-4 rounded border border-yellow-500/20">
                  <h4 className="font-medium text-yellow-300 mb-3 flex items-center">
                    <ICONS.Video className="w-5 h-5 mr-2" />
                    {t('system.changelog.sceneDetection.title')}
                  </h4>
                  <ul className="text-gray-300 space-y-2 text-sm ml-7 list-disc">
                    <li>{t('system.changelog.sceneDetection.frameAccuracy')}</li>
                    <li>{t('system.changelog.sceneDetection.extraction')}</li>
                    <li>{t('system.changelog.sceneDetection.boundaries')}</li>
                  </ul>
                </div>

                {/* UI/UX Improvements */}
                <div className="bg-gray-800/50 p-4 rounded border border-indigo-500/20">
                  <h4 className="font-medium text-indigo-300 mb-3 flex items-center">
                    <ICONS.Settings className="w-5 h-5 mr-2" />
                    {t('system.changelog.uiux.title')}
                  </h4>
                  <ul className="text-gray-300 space-y-2 text-sm ml-7 list-disc">
                    <li>{t('system.changelog.uiux.collapsible')}</li>
                    <li>{t('system.changelog.uiux.projectMemory')}</li>
                    <li>{t('system.changelog.uiux.grouping')}</li>
                    <li>{t('system.changelog.uiux.errorMessages')}</li>
                    <li>{t('system.changelog.uiux.bugfixes')}</li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-purple-500/30">
                <p className="text-sm text-purple-400">
                  📄 {t('system.changelog.fullChangelog')}: <code className="text-green-300 bg-gray-800 px-2 py-1 rounded">CHANGELOG_v2.0.md</code>
                </p>
              </div>
            </div>

            {/* Previous Versions */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                {t('system.changelog.previousVersions')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded">
                  <div>
                    <span className="text-white font-medium">v1.2.0</span>
                    <span className="text-gray-400 text-sm ml-3">November 2025</span>
                  </div>
                  <span className="text-gray-400 text-sm">{t('system.changelog.v120.description')}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded">
                  <div>
                    <span className="text-white font-medium">v1.1.0</span>
                    <span className="text-gray-400 text-sm ml-3">October 2025</span>
                  </div>
                  <span className="text-gray-400 text-sm">{t('system.changelog.v110.description')}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded">
                  <div>
                    <span className="text-white font-medium">v1.0.5</span>
                    <span className="text-gray-400 text-sm ml-3">October 2025</span>
                  </div>
                  <span className="text-gray-400 text-sm">{t('system.changelog.v105.description')}</span>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl mx-4 h-[80vh] overflow-hidden flex">
        {/* Left Sidebar - Tabs */}
        <div className="w-64 bg-gray-700 border-r border-gray-600 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-600">
            <h2 className="text-xl font-bold text-white">
              {t('system.title')}
            </h2>
          </div>

          {/* Tabs */}
          <div className="flex-1 p-4 space-y-2">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              
              // Check access rights based on flags
              let isDisabled = false;
              if (tab.requiresSuperAdmin) {
                // Only Super Admin
                isDisabled = currentRole !== 'Super Admin';
              } else if (tab.requiresAdminOrHigher) {
                // Admin, Producer, or Super Admin
                isDisabled = currentRole !== 'Super Admin' && currentRole !== 'Admin' && currentRole !== 'Producer';
              }
              
              return (
                <button
                  key={tab.id}
                  onClick={() => !isDisabled && setActiveTab(tab.id)}
                  disabled={isDisabled}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'text-indigo-400 bg-indigo-900/20 border border-indigo-600'
                      : isDisabled
                      ? 'text-gray-500 cursor-not-allowed'
                      : 'text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <tab.icon className="w-5 h-5 mr-3" />
                  <span className="flex-1 text-left">{tab.label}</span>
                  {isDisabled && <ICONS.Lock className="w-4 h-4" />}
                </button>
              );
            })}
          </div>

        </div>

         {/* Right Content */}
         <div className="flex-1 flex flex-col">
           {/* Content Header */}
           <div className="p-6 border-b border-gray-600">
             <div className="flex items-center justify-between">
               <div className="flex items-center">
                 {(() => {
                   const currentTab = tabs.find(tab => tab.id === activeTab);
                   const IconComponent = currentTab?.icon;
                   return IconComponent ? (
                     <IconComponent className="w-6 h-6 text-indigo-400 mr-3" />
                   ) : null;
                 })()}
                 <div>
                   <h3 className="text-lg font-semibold text-white">
                     {tabs.find(tab => tab.id === activeTab)?.label}
                   </h3>
                   <p className="text-sm text-gray-400">
                     {tabs.find(tab => tab.id === activeTab)?.description}
                   </p>
                 </div>
               </div>
               
               {/* Close Button */}
               <button
                 onClick={onClose}
                 className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
               >
                 <ICONS.X className="w-5 h-5" />
               </button>
             </div>
           </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {renderTabContent()}
          </div>
        </div>
      </div>

    </div>
  );
};

export default SystemMenu;